<template>
  <sme-card large title="Employee Impact">
    <app-loading v-if="loading" />

    <template v-else>
      <!-- Sentiment Stats Row -->
      <b-row v-if="hasSentimentData" class="mb-4">
        <b-col md="4" class="mb-3 mb-md-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="lastStressedPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel less stressed"
              background-color="core-1-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="stressedDataset.values.length"
              :labels="timeLabels"
              :datasets="stressedDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>
        <b-col md="4" class="mb-3 mb-md-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="lastControlPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel more in control"
              background-color="core-2-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="controlDataset.values.length"
              :labels="timeLabels"
              :datasets="controlDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>
        <b-col md="4">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="lastEmployerPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel positive about employer"
              background-color="core-3-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="employerDataset.values.length"
              :labels="timeLabels"
              :datasets="employerDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>
      </b-row>

      <!-- Quotes Section -->
      <div v-if="quotes.length" class="quotes-section">
        <h6 class="mb-3">What your employees are saying</h6>
        <b-row>
          <b-col v-for="(quote, index) in displayedQuotes" :key="index" md="4" class="mb-3">
            <div class="quote-card p-3 bg-light rounded h-100">
              <p class="mb-0 fst-italic">"{{ quote }}"</p>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Empty State -->
      <p v-if="!hasSentimentData && !quotes.length" class="my-2 text-center text-muted">
        No impact data available yet.
      </p>
    </template>
  </sme-card>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import { toNumber } from '@/utils/common';

const loading = ref(true);
const timeLabels = ref({ values: [] });
const stressedDataset = ref({ values: [], name: 'Less stressed', lineColor: PaletteColors['core-1'] });
const controlDataset = ref({ values: [], name: 'More in control', lineColor: PaletteColors['core-2'] });
const employerDataset = ref({ values: [], name: 'Positive about employer', lineColor: PaletteColors['core-3'] });
const quotes = ref([]);

const calculatePercentages = (positiveValues, totalValues) => {
  if (!positiveValues || !totalValues || positiveValues.length !== totalValues.length) {
    return [];
  }
  return positiveValues.map((positive, i) => {
    const total = totalValues[i] || 0;
    return total > 0 ? Math.round((positive / total) * 1000) / 10 : 0;
  });
};

const lastStressedPercent = computed(() => {
  const values = stressedDataset.value.values;
  return values.length > 0 ? values[values.length - 1] : 0;
});

const lastControlPercent = computed(() => {
  const values = controlDataset.value.values;
  return values.length > 0 ? values[values.length - 1] : 0;
});

const lastEmployerPercent = computed(() => {
  const values = employerDataset.value.values;
  return values.length > 0 ? values[values.length - 1] : 0;
});

const hasSentimentData = computed(() => {
  return (
    stressedDataset.value.values.length > 0 ||
    controlDataset.value.values.length > 0 ||
    employerDataset.value.values.length > 0
  );
});

const displayedQuotes = computed(() => quotes.value.slice(0, 6));

const getImpactData = async () => {
  loading.value = true;

  try {
    const timeSeries = 'm2m';

    const [stressResponse, controlResponse, employerResponse, quotesResponse] = await Promise.all([
      ApiClient.getPortalSentimentStress(timeSeries),
      ApiClient.getPortalSentimentControl(timeSeries),
      ApiClient.getPortalSentimentEmployer(timeSeries),
      ApiClient.getPortalQuotes(),
    ]);

    // Process stress data - API returns less_stress and total_responses
    if (stressResponse?.results?.length) {
      const time = stressResponse.results.find(result => result.id === 'time');
      const lessStress = stressResponse.results.find(result => result.id === 'less_stress');
      const totalResponses = stressResponse.results.find(result => result.id === 'total_responses');

      if (time) {
        timeLabels.value = time;
      }

      if (lessStress && totalResponses) {
        const percentages = calculatePercentages(lessStress.values, totalResponses.values);
        stressedDataset.value = {
          values: percentages,
          name: 'Less stressed',
          lineColor: PaletteColors['core-1'],
        };
      }
    }

    // Process control data - API returns more_control and total_responses
    if (controlResponse?.results?.length) {
      const moreControl = controlResponse.results.find(result => result.id === 'more_control');
      const totalResponses = controlResponse.results.find(result => result.id === 'total_responses');

      if (moreControl && totalResponses) {
        const percentages = calculatePercentages(moreControl.values, totalResponses.values);
        controlDataset.value = {
          values: percentages,
          name: 'More in control',
          lineColor: PaletteColors['core-2'],
        };
      }
    }

    // Process employer data - API returns positive_sentiment and total_responses
    if (employerResponse?.results?.length) {
      const positiveSentiment = employerResponse.results.find(result => result.id === 'positive_sentiment');
      const totalResponses = employerResponse.results.find(result => result.id === 'total_responses');

      if (positiveSentiment && totalResponses) {
        const percentages = calculatePercentages(positiveSentiment.values, totalResponses.values);
        employerDataset.value = {
          values: percentages,
          name: 'Positive about employer',
          lineColor: PaletteColors['core-3'],
        };
      }
    }

    // Process quotes - API returns array of strings
    if (quotesResponse?.data?.length) {
      quotes.value = quotesResponse.data;
    }
  } catch (error) {
    console.error('Failed to fetch impact data:', error);
  }

  loading.value = false;
};

onBeforeMount(() => getImpactData());
</script>

<style scoped>
.quote-card {
  border-left: 3px solid var(--core-3);
}

.quotes-section h6 {
  color: var(--gray-700);
  font-weight: 600;
}
</style>
